import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { CopyIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import { IntakeKey } from "@/hooks/api/use-intake-keys";
import { API_URL } from "@/runtime-env";

/**
 * The brief a customer pastes into their coding agent.
 *
 * Deliberately short. It states the one architectural constraint that matters
 * (the secret never reaches the browser), the exact wire format, and the three
 * response cases. Everything beyond that is the customer's own build.
 *
 * The known-answer vector is included because a wrong signing implementation is
 * indistinguishable from a wrong secret at the API - it lets their agent prove
 * its signing is correct before it ever touches a live key.
 */
function buildBrief(keyId: string, endpoint: string): string {
	return `Add a "Call me" callback form to this website, wired to the AdaptiveX intake API.

## How it must be wired

Browser form -> an endpoint on THIS website's own backend -> AdaptiveX.
The browser must never call AdaptiveX directly.

## Credentials (server-side only)

AX_INTAKE_KEY_ID=${keyId}
AX_INTAKE_SECRET=<copied from the AdaptiveX dashboard when the key was created>

Never put the secret anywhere the browser can read it: not in NEXT_PUBLIC_*,
VITE_*, REACT_APP_*, PUBLIC_*, a data attribute, or any file served to the
browser. Anyone with the secret can make our AI agent phone arbitrary numbers at
our expense.

## The request to send from our backend

POST ${endpoint}
Content-Type: application/json
X-AX-Key: <AX_INTAKE_KEY_ID>
X-AX-Timestamp: <unix time in SECONDS, integer>
X-AX-Signature: sha256=<hex signature, see below>
Idempotency-Key: <a UUID, one per submission attempt>

Body (only "phone" is required):

{
  "phone": "+447700900123",
  "name": "Alex Doe",
  "email": "alex@example.com",
  "message": "Interested in the pro plan",
  "source": "homepage-hero",
  "consent": { "text": "<the exact checkbox label the visitor ticked>" }
}

## Signing

signature = HMAC-SHA256(key: AX_INTAKE_SECRET, message: "<timestamp>.<body>"), hex

- The timestamp is in SECONDS, not milliseconds.
- Build the JSON body string ONCE, sign that exact string, and send that exact
  string. Do not sign a serialisation and then hand an object to an HTTP client
  that serialises it again - the bytes differ and every request will 401.

Check your implementation reproduces this before using the real key:

  secret    = axsk_TESTSECRET_do_not_use
  timestamp = 1753876800
  body      = {"phone":"+447700900123","name":"Alex Doe","source":"homepage-hero"}
  signature = b3172c51c841459e61c4b220c5919748ff473e130c4e3aad090e37ca0967245f

That secret is for verification only. Never ship it.

## Handling the response

A 201 returns { "requestId": ..., "status": ..., "callingNow": ... }

- "queued"    -> booked. callingNow true  -> "calling you now".
                 callingNow false -> "we'll call you shortly" (it arrived
                 outside our calling hours). Do not invent a specific time.
- "duplicate" -> ALSO SUCCESS. They're already going to be called; a second call
                 was suppressed. Show the same confirmation. Do not retry.
- "rejected"  -> show a polite failure; "reason" says why.

Retry only on 503 or a network error, at most twice. On each retry:

- keep the SAME Idempotency-Key (this is what prevents a second call), but
- RE-SIGN with a fresh timestamp at least 1 second later than the previous
  attempt. Timestamps have 1-second resolution, so a fast retry would otherwise
  reuse the same signature and be rejected as a replay with 409.

Never retry 409 or 429. Never write a retry loop.
Never show the visitor AdaptiveX's raw error text - log it server-side.

## The form itself

Phone required. A consent checkbox is required to submit, and its exact label
text goes in consent.text. Disable the submit button while the request is in
flight so a double-click can't submit twice.`;
}

export function SetupInstructionsDialog({
	open,
	onClose,
	intakeKey,
}: {
	open: boolean;
	onClose: () => void;
	intakeKey: IntakeKey | null;
}): React.JSX.Element {
	// API_URL already includes the /api prefix.
	const endpoint = `${API_URL ?? "https://your-adaptivex-host/api"}/intake/callback-requests`;
	const brief = React.useMemo(
		() => buildBrief(intakeKey?.keyId ?? "axi_live_...", endpoint),
		[intakeKey?.keyId, endpoint]
	);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(brief);
			toast.success("Instructions copied - paste them into your coding agent");
		} catch {
			toast.error("Could not copy - select the text and copy manually");
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			sx={{
				"& .MuiDialog-container": {
					"& .MuiPaper-root": {
						width: "100%",
					},
				},
			}}
		>
			<DialogTitle>
				Setup instructions
				<IconButton color="inherit" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ py: 4!, pt: "8px !important" }}>
				<Stack spacing={2}>
					<Typography variant="body2" color="text.secondary">
						Copy this and paste it into your coding assistant (Claude Code, Cursor, Copilot). It already contains your
						key ID and endpoint. Your developer only needs to add the secret.
					</Typography>

					<Alert severity="warning">
						The signing secret goes in your server environment only. If it reaches the browser, anyone can make your
						agent dial any number.
					</Alert>

					<Box
						component="pre"
						sx={{
							p: 2,
							borderRadius: 1,
							bgcolor: "background.level1",
							fontFamily: "monospace",
							fontSize: 12,
							lineHeight: 1.55,
							maxHeight: 380,
							overflow: "auto",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
							m: 0,
						}}
					>
						{brief}
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={onClose}>
						Close
					</Button>
					<Button color="primary" variant="contained" onClick={copy} startIcon={<CopyIcon />}>
						Copy instructions
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
